import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout } from '@/components/layout/Layout'
import { CommandCentreLayout } from '@/components/layout/CommandCentreLayout'
import { AuthProvider, useAuth } from '@/hooks/use-auth'
import { useUiMode } from '@/hooks/use-ui-mode'
import { useLedgerState } from '@/hooks/use-ledger-state'
import { LoginPage } from '@/pages/LoginPage'
import { AcceptInvitePage } from '@/pages/AcceptInvitePage'
import { AuthCallbackPage } from '@/pages/AuthCallbackPage'
import { ProvisioningPage } from '@/pages/ProvisioningPage'
import DevicePairPage from '@/pages/DevicePairPage'
import { DesktopLoginPage } from '@/pages/DesktopLoginPage'
import { OnboardingPage } from '@/pages/OnboardingPage'
import { CancellationBanner } from '@/components/CancellationBanner'
import { Spinner } from '@/components/ui/spinner'
import { api } from '@/api/client'
import { isDesktop } from '@/lib/platform'
import { isShowcaseMode } from '@/lib/showcase-mode'
import { WifiOff } from 'lucide-react'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { LayoutWideContext, useLayoutWideState } from '@/hooks/use-layout-wide'

/**
 * Wrapper around dynamic import that retries once via page reload when a chunk
 * fails to load (e.g. after a deployment changes asset hashes).
 */
function lazyRetry(factory: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const key = 'ledger:chunk-retry'
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1')
        window.location.reload()
        // Return a never-resolving promise so React doesn't render the error
        return new Promise<{ default: React.ComponentType }>(() => {})
      }
      sessionStorage.removeItem(key)
      throw err
    }),
  )
}

// Lazy-loaded pages — chunks are fetched on first navigation
const Dashboard = lazyRetry(() => import('@/pages/Dashboard').then(m => ({ default: m.Dashboard })))
const ReadinessPage = lazyRetry(() => import('@/pages/ReadinessPage').then(m => ({ default: m.ReadinessPage })))
const AccountsPage = lazyRetry(() => import('@/pages/AccountsPage').then(m => ({ default: m.AccountsPage })))
const JournalEntriesPage = lazyRetry(() => import('@/pages/JournalEntriesPage').then(m => ({ default: m.JournalEntriesPage })))
const NewJournalEntryPage = lazyRetry(() => import('@/pages/NewJournalEntryPage').then(m => ({ default: m.NewJournalEntryPage })))
const TeamPage = lazyRetry(() => import('@/pages/TeamPage').then(m => ({ default: m.TeamPage })))
const AdminPage = lazyRetry(() => import('@/pages/AdminPage').then(m => ({ default: m.AdminPage })))
const SiteAdminPage = lazyRetry(() => import('@/pages/SiteAdminPage').then(m => ({ default: m.SiteAdminPage })))
const NewOrganisationPage = lazyRetry(() => import('@/pages/NewOrganisationPage').then(m => ({ default: m.NewOrganisationPage })))
const BillingPage = lazyRetry(() => import('@/pages/BillingPage'))
const InventoryPage = lazyRetry(() => import('@/pages/InventoryPage').then(m => ({ default: m.InventoryPage })))
const QuotesPage = lazyRetry(() => import('@/pages/QuotesPage').then(m => ({ default: m.QuotesPage })))
const PurchaseOrdersPage = lazyRetry(() => import('@/pages/PurchaseOrdersPage').then(m => ({ default: m.PurchaseOrdersPage })))
const CreditNotesPage = lazyRetry(() => import('@/pages/CreditNotesPage').then(m => ({ default: m.CreditNotesPage })))
const ReportsPage = lazyRetry(() => import('@/pages/ReportsPage').then(m => ({ default: m.ReportsPage })))
const ContactsPage = lazyRetry(() => import('@/pages/ContactsPage').then(m => ({ default: m.ContactsPage })))
const FixedAssetsPage = lazyRetry(() => import('@/pages/FixedAssetsPage').then(m => ({ default: m.FixedAssetsPage })))
const BankingPage = lazyRetry(() => import('@/pages/BankingPage').then(m => ({ default: m.BankingPage })))
const JobsPage = lazyRetry(() => import('@/pages/JobsPage').then(m => ({ default: m.JobsPage })))
const InvoicesPage = lazyRetry(() => import('@/pages/InvoicesPage').then(m => ({ default: m.InvoicesPage })))
const BillsPage = lazyRetry(() => import('@/pages/BillsPage').then(m => ({ default: m.BillsPage })))
const InTrayPage = lazyRetry(() => import('@/pages/InTrayPage').then(m => ({ default: m.InTrayPage })))
const GstFilingPage = lazyRetry(() => import('@/pages/GstFilingPage').then(m => ({ default: m.GstFilingPage })))
const ExpenseClaimsPage = lazyRetry(() => import('@/pages/ExpenseClaimsPage').then(m => ({ default: m.ExpenseClaimsPage })))
const UtilitiesPage = lazyRetry(() => import('@/pages/UtilitiesPage').then(m => ({ default: m.UtilitiesPage })))
const BillComparisonPage = lazyRetry(() => import('@/pages/BillComparisonPage').then(m => ({ default: m.BillComparisonPage })))
const SharedInvoicePage = lazyRetry(() => import('@/pages/SharedInvoicePage').then(m => ({ default: m.SharedInvoicePage })))
const SharedQuotePage = lazyRetry(() => import('@/pages/SharedQuotePage').then(m => ({ default: m.SharedQuotePage })))
const SharedCreditNotePage = lazyRetry(() => import('@/pages/SharedCreditNotePage').then(m => ({ default: m.SharedCreditNotePage })))
const CustomerPortalPage = lazyRetry(() => import('@/pages/CustomerPortalPage').then(m => ({ default: m.CustomerPortalPage })))
const TimesheetPage = lazyRetry(() => import('@/pages/TimesheetPage').then(m => ({ default: m.TimesheetPage })))
const TaxDashboardPage = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.TaxDashboardPage })))
const TaxProfilePage = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.TaxProfilePage })))
const TaxPeriodsPage = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.TaxPeriodsPage })))
const TaxAdjustmentsPage = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.TaxAdjustmentsPage })))
const ApportionmentPage = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.ApportionmentPage })))
const IncomeTaxPage = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.IncomeTaxPage })))
const ProvisionalTaxPage = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.ProvisionalTaxPage })))
const EmployeeDeductionsPage = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.EmployeeDeductionsPage })))
const TaxSnapshotPage = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.TaxSnapshotPage })))
const FilingPacksPage = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.FilingPacksPage })))
const TaxCalendarPage = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.TaxCalendarPage })))
const GstFilingV3Page = lazyRetry(() => import('@/pages/tax').then(m => ({ default: m.GstFilingV3Page })))

function LazySpinner() {
  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center">
      <Spinner className="h-6 w-6 text-primary" />
    </div>
  )
}

function AppRoutes() {
  const auth = useAuth()
  const ledger = useLedgerState()
  const layoutWide = useLayoutWideState()
  const [provisioningAccounts, setProvisioningAccounts] = useState(false)
  const [provisioningOrgName, setProvisioningOrgName] = useState<string>('')
  const provisioningInFlight = useRef(false)
  const [onboardingDone, setOnboardingDone] = useState(
    () => isShowcaseMode() && sessionStorage.getItem('ledger:onboarding-done') === '1'
  )
  const [orgLifecycleState, setOrgLifecycleState] = useState<string | null>(null)
  const [orgLifecycleInfo, setOrgLifecycleInfo] = useState<{ purge_at?: string; cancelled_at?: string } | null>(null)
  const [orgFeatures, setOrgFeatures] = useState<Record<string, boolean> | null>(null)
  const uiMode = useUiMode(auth.user, () => {
    // Propagate user update back to auth context
    auth.refreshUser()
  })

  useEffect(() => {
    if (!auth.isAuthenticated || !ledger.isReady) return

    let active = true

    const ensureAccountsProvisioned = async (orgId?: string) => {
      if (provisioningInFlight.current || !active) return

      provisioningInFlight.current = true
      try {
        const selectedOrgId = orgId || (await api.getActiveOrgId())
        const organisation = await api.getOrganisation(selectedOrgId)
        setProvisioningOrgName(organisation.name || 'My Organisation')

        // Track org lifecycle state for gating
        setOrgLifecycleState(organisation.lifecycle_state ?? 'active')
        setOrgLifecycleInfo({
          purge_at: organisation.purge_at ?? undefined,
          cancelled_at: organisation.cancelled_at ?? undefined,
        })

        // Fetch feature flags so the sidebar can hide gated items
        api.getOrgFeatures().then(res => setOrgFeatures(res.features)).catch(() => {})

        const needsProvisioning = (organisation.accounts_count ?? 0) == 0 || organisation.fully_provisioned === false

        // Only admins/owners can provision accounts
        const userRole = auth.user?.membership_role
        const canProvision = userRole === 'owner' || userRole === 'admin' || auth.user?.role === 'site_admin'

        if (!needsProvisioning || !canProvision || !active) return

        setProvisioningAccounts(true)

        await api.provisionOrganisationAccounts(selectedOrgId, organisation.default_currency)
      } catch {
        // If provisioning fails, allow the app to continue and surface API errors on pages.
      } finally {
        if (active) {
          setProvisioningAccounts(false)
        }
        provisioningInFlight.current = false
      }
    }

    void ensureAccountsProvisioned()
    const unsubscribe = api.onOrgChange((orgId) => {
      void ensureAccountsProvisioned(orgId)
    })

    return () => {
      active = false
      unsubscribe()
    }
  }, [auth.isAuthenticated, auth.user?.membership_role, auth.user?.role, ledger.isReady])

  // Global loading while auth + ledger bootstrap
  if (auth.loading || (ledger.loading && !ledger.readiness)) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="h-8 w-8 text-primary" />
          <p className="text-sm text-muted-foreground">Connecting to <span className="font-semibold text-foreground">Ledger.</span> <span className="font-light">Core</span>…</p>
        </div>
      </div>
    )
  }

  if (provisioningAccounts) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex max-w-md flex-col items-center gap-4 rounded-lg border border-border bg-card px-8 py-10 text-center">
          <Spinner className="h-9 w-9 text-primary" />
          <h1 className="text-lg font-semibold">Provisioning Accounts</h1>
          <p className="text-sm text-muted-foreground">
            Setting up foundational accounts for {provisioningOrgName} so you can hit the ground running.
          </p>
        </div>
      </div>
    )
  }

  // Public route: accept-invite (always accessible)
  // Rendered outside layout when not authenticated
  if (!auth.isAuthenticated) {
    return (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/pair" element={<DevicePairPage />} />
        <Route path="/share/invoices/:token" element={<SharedInvoicePage />} />
        <Route path="/share/quotes/:token" element={<SharedQuotePage />} />
        <Route path="/share/credit-notes/:token" element={<SharedCreditNotePage />} />
        <Route path="/portal/:token" element={<CustomerPortalPage />} />
        {/* System not provisioned → show provisioning first */}
        {auth.setupStatus && !auth.setupStatus.provisioned ? (
          isDesktop() ? (
            <Route path="*" element={
              <div className="flex h-screen items-center justify-center bg-background">
                <div className="mx-auto max-w-md rounded-lg border border-border bg-card p-8 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                    <WifiOff className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="font-mono text-sm text-muted-foreground">ERR_UPSTREAM_GENESIS_INCOMPLETE</p>
                  <p className="mt-1 font-mono text-xs text-muted-foreground/60">0xE001</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    Reload
                  </button>
                </div>
              </div>
            } />
          ) : (
            <>
              <Route path="/provisioning" element={<ProvisioningPage onComplete={ledger.refresh} />} />
              <Route path="*" element={<Navigate to="/provisioning" replace />} />
            </>
          )
        ) : auth.needsSetup ? (
          <Route path="*" element={
            <div className="flex h-screen items-center justify-center bg-background">
              <div className="mx-auto max-w-md rounded-lg border border-border bg-card p-8 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <WifiOff className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="font-mono text-sm text-muted-foreground">ERR_UPSTREAM_GENESIS_INCOMPLETE</p>
                <p className="mt-1 font-mono text-xs text-muted-foreground/60">0xE002</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  Reload
                </button>
              </div>
            </div>
          } />
        ) : isDesktop() ? (
          <Route path="*" element={<DesktopLoginPage onAuthenticated={() => auth.refreshUser()} />} />
        ) : (
          <>
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        )}
      </Routes>
    )
  }

  // One-time onboarding experience for new users
  if (auth.isAuthenticated && !auth.user?.onboarded && !onboardingDone && ledger.isReady) {
    return (
      <OnboardingPage
        onComplete={(uiMode) => {
          auth.completeOnboarding(uiMode === 'classic' ? 'classic' : undefined)
          setOnboardingDone(true)
          if (isShowcaseMode()) sessionStorage.setItem('ledger:onboarding-done', '1')
        }}
      />
    )
  }

  // Organisation unavailable (detached or admin-suspended)
  if (auth.isAuthenticated && (orgLifecycleState === 'detached' || orgLifecycleState === 'admin_suspended') && !auth.user?.site_admin) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="mx-auto max-w-md rounded-lg border border-border bg-card p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold">Organisation Unavailable</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            This organisation is unavailable. Please contact support at{' '}
            <a href="mailto:hey@ledgerhq.nz" className="text-primary hover:underline">hey@ledgerhq.nz</a>
          </p>
          <button
            onClick={auth.logout}
            className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Sign Out
          </button>
        </div>
      </div>
    )
  }

  // Organisation cancelled - show export prompt
  if (auth.isAuthenticated && orgLifecycleState === 'cancelled' && !auth.user?.site_admin) {
    const daysUntilPurge = orgLifecycleInfo?.purge_at
      ? Math.max(0, Math.ceil((new Date(orgLifecycleInfo.purge_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
      : null
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="mx-auto max-w-md rounded-lg border border-border bg-card p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/20">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold">Organisation Cancelled</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            This organisation has been cancelled.
            {daysUntilPurge !== null && (
              <> Your data will be permanently deleted in <strong>{daysUntilPurge} days</strong>.</>
            )}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            You can still export your data or reactivate your organisation.
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <button
              onClick={async () => {
                try {
                  const orgId = await api.getActiveOrgId()
                  await api.reactivateOrganisation(orgId)
                  window.location.reload()
                } catch { /* handled by caller */ }
              }}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Reactivate Organisation
            </button>
            <button
              onClick={auth.logout}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    )
  }

  // API connectivity failure
  if (ledger.error && !ledger.readiness) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="mx-auto max-w-md rounded-lg border border-border bg-card p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <WifiOff className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="font-mono text-sm text-muted-foreground">ERR_BAD_GENESIS</p>
          <p className="mt-1 font-mono text-xs text-muted-foreground/60">0xC000</p>
          <button
            onClick={ledger.refresh}
            className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const defaultRoute = ledger.isReady ? '/' : '/provisioning'
  const canManageTeam = auth.user?.role === 'site_admin' || auth.user?.role === 'admin'
    || (auth.user?.membership_role && ['owner', 'admin'].includes(auth.user.membership_role))

  if (uiMode.uiMode === 'classic') {
    return (
      <div className="theme-classic">
        <CommandCentreLayout
          user={auth.user}
          orgName={provisioningOrgName}
          features={orgFeatures}
          graduatedTabs={uiMode.graduatedTabs}
          showNudge={uiMode.shouldShowNudge}
          daysRemaining={uiMode.classicModeDaysRemaining}
          expiryDate={uiMode.classicModeExpiryDate}
          declinedCount={auth.user?.guided_transition_declined_count ?? 0}
          showWelcome={!auth.user?.classic_welcome_dismissed_at}
          classicModeSource={auth.user?.classic_mode_source ?? null}
          onAcceptTransition={uiMode.acceptGuidedTransition}
          onDeclineTransition={uiMode.declineGuidedTransition}
          onDismissWelcome={async () => {
            try {
              await api.updatePreferences({
                classic_welcome_dismissed_at: new Date().toISOString(),
              })
              await auth.refreshUser()
            } catch { /* non-critical */ }
          }}
          onLogout={auth.logout}
          onSwitchToModern={async () => {
            localStorage.setItem('ledger:ui-mode', 'standard')
            await uiMode.setUiMode('standard').catch(() => {})
            await api.refreshToken().catch(() => {})
            window.location.replace('/')
          }}
        >
          <Suspense fallback={<LazySpinner />}>
          <Routes>
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/journal-entries" element={<JournalEntriesPage />} />
            <Route path="/journal-entries/new" element={<NewJournalEntryPage />} />
            <Route path="/invoices" element={<InvoicesPage />} />
            <Route path="/quotes" element={<QuotesPage />} />
            <Route path="/credit-notes" element={<CreditNotesPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/banking" element={<BankingPage />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="/time-tracking" element={<TimesheetPage />} />
            <Route path="/expense-claims" element={<ExpenseClaimsPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/fixed-assets" element={<FixedAssetsPage />} />
            <Route path="/utilities" element={<UtilitiesPage />} />
            <Route path="/bill-comparison" element={<BillComparisonPage />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/site-admin" element={<SiteAdminPage />} />
            <Route path="/pair" element={<DevicePairPage />} />
            <Route path="*" element={<Navigate to="/accounts" replace />} />
          </Routes>
          </Suspense>
        </CommandCentreLayout>
      </div>
    )
  }

  return (
    <LayoutWideContext.Provider value={layoutWide}>
    <Layout
      ledger={ledger}
      user={auth.user}
      onLogout={auth.logout}
      features={orgFeatures}
      cancellationBanner={orgLifecycleState === 'cancellation_pending' ? <CancellationBanner /> : undefined}
      classicMode={{
        enabled: false,
        expired: uiMode.classicModeExpired,
        daysRemaining: uiMode.classicModeDaysRemaining,
        onToggle: async (enabled) => {
          const newMode = enabled ? 'classic' : 'standard'
          localStorage.setItem('ledger:ui-mode', newMode)
          await uiMode.setUiMode(newMode).catch(() => {})
          await api.refreshToken().catch(() => {})
          window.location.reload()
        },
      }}
    >
      {uiMode.showExpiryBanner && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950">
          <div className="flex items-center justify-between">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Your Command Centre (classic) layout period has ended. You're now using the modern Ledger Core interface.
            </p>
            <button
              onClick={uiMode.dismissExpiryBanner}
              className="ml-4 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <Suspense fallback={<LazySpinner />}>
      <Routes>
        <Route path="/" element={ledger.isReady ? <Dashboard /> : <Navigate to="/provisioning" replace />} />
        <Route path="/readiness" element={<ReadinessPage />} />
        <Route path="/provisioning" element={<ProvisioningPage onComplete={ledger.refresh} />} />
        <Route
          path="/accounts"
          element={ledger.isReady ? <AccountsPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/journal-entries"
          element={ledger.isReady ? <JournalEntriesPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/journal-entries/new"
          element={ledger.isReady ? <NewJournalEntryPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/team"
          element={canManageTeam ? <TeamPage /> : <Navigate to={defaultRoute} replace />}
        />
        <Route
          path="/admin"
          element={canManageTeam ? <AdminPage /> : <Navigate to={defaultRoute} replace />}
        />
        <Route
          path="/site-admin"
          element={auth.user?.role === 'site_admin' ? <SiteAdminPage /> : <Navigate to={defaultRoute} replace />}
        />
        <Route
          path="/organisations/new"
          element={ledger.isReady ? <NewOrganisationPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/billing"
          element={ledger.isReady ? <BillingPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/inventory"
          element={ledger.isReady ? <InventoryPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/quotes"
          element={ledger.isReady ? <QuotesPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/invoices"
          element={ledger.isReady ? <InvoicesPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/bills"
          element={ledger.isReady ? <BillsPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/in-tray"
          element={ledger.isReady ? <InTrayPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/gst-filing"
          element={ledger.isReady ? <GstFilingPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax"
          element={ledger.isReady ? <TaxDashboardPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax/profile"
          element={ledger.isReady ? <TaxProfilePage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax/periods"
          element={ledger.isReady ? <TaxPeriodsPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax/adjustments"
          element={ledger.isReady ? <TaxAdjustmentsPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax/apportionment"
          element={ledger.isReady ? <ApportionmentPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax/income-tax"
          element={ledger.isReady ? <IncomeTaxPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax/provisional"
          element={ledger.isReady ? <ProvisionalTaxPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax/deductions"
          element={ledger.isReady ? <EmployeeDeductionsPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax/snapshot"
          element={ledger.isReady ? <TaxSnapshotPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax/filing-packs"
          element={ledger.isReady ? <FilingPacksPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax/calendar"
          element={ledger.isReady ? <TaxCalendarPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/tax/gst"
          element={ledger.isReady ? <GstFilingV3Page /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/purchase-orders"
          element={ledger.isReady ? <PurchaseOrdersPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/credit-notes"
          element={ledger.isReady ? <CreditNotesPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/reports"
          element={ledger.isReady ? <ReportsPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/contacts"
          element={ledger.isReady ? <ContactsPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/fixed-assets"
          element={ledger.isReady ? <FixedAssetsPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/banking"
          element={ledger.isReady ? <BankingPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/jobs"
          element={ledger.isReady ? <JobsPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/time-tracking"
          element={ledger.isReady ? <TimesheetPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/expense-claims"
          element={ledger.isReady ? <ExpenseClaimsPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/utilities"
          element={ledger.isReady ? <UtilitiesPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route
          path="/bill-comparison"
          element={ledger.isReady ? <BillComparisonPage /> : <Navigate to="/provisioning" replace />}
        />
        <Route path="/share/invoices/:token" element={<SharedInvoicePage />} />
        <Route path="/share/quotes/:token" element={<SharedQuotePage />} />
        <Route path="/share/credit-notes/:token" element={<SharedCreditNotePage />} />
        <Route path="/portal/:token" element={<CustomerPortalPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/pair" element={<DevicePairPage />} />
        <Route
          path="/ui/components/onboarding"
          element={<OnboardingPage preview onComplete={() => window.history.back()} />}
        />
        <Route path="*" element={<Navigate to={defaultRoute} replace />} />
      </Routes>
      </Suspense>
    </Layout>
    </LayoutWideContext.Provider>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}
